import { Client } from 'src/admin/users/types';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useClients } from 'src/clients/hooks/useClients';
import { useFamily } from 'src/clients/hooks/useFamily';

export type FamilyMember = {
  clientId: string;
  client: Client;
  isMain: boolean;
};

export function useFamilyMembers({ enabled = true }: { enabled?: boolean } = {}): {
  members: FamilyMember[];
  hasOtherMembers: boolean;
  isLoading: boolean;
} {
  const { client, clientId } = useCurrentClient();
  const familyId = client?.familyId;

  const { family, isLoading: isFamilyLoading } = useFamily(enabled ? familyId : undefined);
  const { clients, isLoading: isClientsLoading } = useClients(
    enabled && familyId ? { familyId } : {},
    { enabled },
  );

  const isLoading = isFamilyLoading || isClientsLoading;

  if (!familyId || !family) {
    return { members: [], hasOtherMembers: false, isLoading };
  }

  const clientById = Object.fromEntries(clients.map(c => [c.id, c]));

  const mainClientId = family.mainFamilyMember.clientId;
  const secondaryIds = family.secondaryFamilyMembers.map(m => m.clientId);

  const mainClient = clientById[mainClientId];

  // Role-model families keep every member in `family.members` with no secondary
  // rows, so union those in to avoid hiding non-primary members when the family
  // is read through the star shape.
  const seen = new Set([mainClientId, ...secondaryIds]);
  const roleModelIds = family.members.map(m => m.clientId).filter(id => !seen.has(id));

  const secondaryMembers: FamilyMember[] = [...secondaryIds, ...roleModelIds]
    .map(id => {
      const c = clientById[id];
      if (!c) return null;
      return { clientId: id, client: c, isMain: false };
    })
    .filter((m): m is FamilyMember => m !== null)
    .sort((a, b) => {
      const aName = `${a.client.firstName} ${a.client.lastName}`.toLowerCase();
      const bName = `${b.client.firstName} ${b.client.lastName}`.toLowerCase();
      return aName.localeCompare(bName);
    });

  const members: FamilyMember[] = [
    ...(mainClient ? [{ clientId: mainClientId, client: mainClient, isMain: true }] : []),
    ...secondaryMembers,
  ];

  const hasOtherMembers = members.some(m => m.clientId !== clientId);

  return { members, hasOtherMembers, isLoading };
}
